import React from "react"
import HTMLSerializer from "@convertkit/slate-html-serializer"
import PlainSerializer from "@convertkit/slate-plain-serializer"

import { serializerRule, styles } from "../slate-nodes"
import typographyStyles from "../utils/typography-styles"

function serialize(
  value,
  {
    type = "text/html",
    target = "email",
    rules = [],
    theme = {},
    find = () => {}
  } = {}
) {
  switch (type) {
    case "text/html": {
      return [
        styles(value.document.toJS()),
        HTML({
          target,
          rules,
          theme,
          find
        })
          .serialize(value)
          .replace(/<outlook-only>/g, "<!--[if mso]>")
          .replace(/<\/outlook-only>/g, "<![endif]-->")
          .replace(/<except-outlook>/g, "<!--[if !mso]>-->")
          .replace(/<\/except-outlook>/g, "<!--<![endif]-->")
          .replace(/&amp;(\w+)=/g, "&$1=") // undo & that are converted to &amp; in links
          .replace(/<outlook-container width="(\d+)">/g, (match, p1) => {
            const width = p1
            return `<!--[if mso]><table><tr><td width="${width}"><![endif]-->`
          })
          .replace(
            /<\/outlook-container>/g,
            "<!--[if mso]></td></tr></table><![endif]-->"
          )
          // Make sure HTML quotes aren't escaped inside Liquid tags
          .replace(/{%([^}]|%(?!}))+%}/g, match => {
            const regex = new RegExp("&quot;")
            if (regex.test(match)) {
              return match.replace(/&quot;/g, '"')
            } else {
              return match
            }
          })
      ]
        .filter(Boolean)
        .join("")
    }
    case "text/plain":
      return PlainSerializer.serialize(value)
  }
}

function Block({ tag, node, ...props }) {
  const Tag = tag

  const style = props.style || {}

  style.textAlign = node.data.get("alignment")

  return <Tag {...props} style={style} />
}

function HTML(options = {}) {
  const { find } = options

  const defaultRules = [
    serializerRule(options),
    {
      serialize(node, children) {
        if (node.object == "block") {
          switch (node.type) {
            case "code-line":
              // We don't need to serialize code because the HTML node handles it.
              return null
            case "style":
              return (
                <style
                  dangerouslySetInnerHTML={{ __html: node.data.get("text") }}
                />
              )

            case "heading-one": {
              const style = {
                ...typographyStyles("h1", find),
                color: options.theme?.h1?.color || find("h1", "color")
              }
              return (
                <Block tag="h1" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "heading-two": {
              const style = {
                ...typographyStyles("h2", find),
                color: options.theme?.h2?.color || find("h2", "color")
              }
              return (
                <Block tag="h2" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "heading-three": {
              const style = {
                ...typographyStyles("h3", find),
                color: options.theme?.h3?.color || find("h3", "color")
              }
              return (
                <Block tag="h3" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "heading-four": {
              const style = {
                ...typographyStyles("h4", find),
                color: options.theme?.h4?.color || find("h4", "color")
              }
              return (
                <Block tag="h4" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "heading-five": {
              const style = {
                ...typographyStyles("h5", find),
                color: options.theme?.h5?.color || find("h5", "color")
              }
              return (
                <Block tag="h5" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "heading-six": {
              const style = {
                ...typographyStyles("h6", find),
                color: options.theme?.h6?.color || find("h6", "color")
              }
              return (
                <Block tag="h6" node={node} style={style}>
                  {children}
                </Block>
              )
            }
            case "horizontal-rule":
              return <hr />
            case "html": {
              return (
                <div
                  dangerouslySetInnerHTML={{ __html: node.data.get("html") }}
                />
              )
            }
            case "caption": {
              const visible = node.getText() !== ""
              return (
                <figcaption
                  style={{
                    textAlign: "center",
                    display: visible ? "block" : "none"
                  }}
                >
                  {children}
                </figcaption>
              )
            }
            case "list-item":
              return <li>{children}</li>
            case "list-item-child":
              return <span>{children}</span>
            case "paragraph":
              return (
                <Block tag="p" node={node}>
                  {children}
                </Block>
              )
            case "ordered-list":
              return <ol style={{ listStylePosition: "inside" }}>{children}</ol>
            case "unordered-list":
              return <ul style={{ listStylePosition: "inside" }}>{children}</ul>
          }
        } else if (node.object == "inline") {
          switch (node.type) {
            case "file": {
              return (
                <a
                  href={node.data.get("url")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              )
            }
            case "link":
              return (
                <a
                  href={node.data.get("href")}
                  data-tag-id={node.data.get("tagId")}
                  trigger-id={node.data.get("triggerId")}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: find("link", "color") }}
                >
                  {children}
                </a>
              )
            case "personalization":
              return (
                <React.Fragment>{`{{ ${node.data.get(
                  "value"
                )} }}`}</React.Fragment>
              )
            case "snippet":
              return (
                <React.Fragment>{`{{ ${node.data.get(
                  "value"
                )} }}`}</React.Fragment>
              )
          }
        } else if (node.object == "mark") {
          switch (node.type) {
            case "bold":
              return <strong>{children}</strong>
            case "italic":
              return <em>{children}</em>
            case "underline":
              return <u>{children}</u>
            case "strikethrough":
              return <s>{children}</s>
            case "color":
              return (
                <span style={{ color: node.data.get("color") }}>
                  {children}
                </span>
              )
            case "font-size":
              return (
                <span style={{ fontSize: node.data.get("fontSize") }}>
                  {children}
                </span>
              )
            case "inline-code":
              return <code>{children}</code>
          }
        } else if (node.object === "string") {
          return children.split("\n").reduce((array, text, i) => {
            if (i != 0) array.push(<br key={i} />)
            array.push(text || "\u200B")
            return array
          }, [])
        }
      }
    }
  ]

  return new HTMLSerializer({
    ...options,
    rules: [...defaultRules, ...(options.rules || [])]
  })
}

export default serialize
